import React from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { List } from 'immutable';
import { connect } from 'react-redux';
import Button from '@saucelabs/ds-button';
import { actions as modalActions } from '@saucelabs/sl-container-modal';
import { Tooltip } from '@saucelabs/ds-tooltip';
import {
  TM_ADMIN_ROLE,
  TM_ORG_ROLES,
  TM_TEAM_ADMIN_ROLE,
  TM_TEAM_LIMIT_VALUE_FEATURE_DISABLED,
} from '@saucelabs/sl-util-config';
import { useFlags } from '@saucelabs/sl-util-launchdarkly';
import { DSThemeContext } from '@saucelabs/ds-theme';
import { AddNonSSOUserModal, constants as inviteConstants, actions as inviteActions } from '@saucelabs/sl-container-invite';
import { records as userRecords } from '@saucelabs/sl-module-user';

const allowToCreateNewTeams = (settings) => (
  !settings.team_limit_reached
);

const teamFeatureEnabled = (settings) => (
  allowToCreateNewTeams(settings) ||
  settings.team_limit !== TM_TEAM_LIMIT_VALUE_FEATURE_DISABLED
);

const filteredOrgRoles = ({ me }) => {
  const allowTeamAdminRole = teamFeatureEnabled(me.organization.settings);
  return TM_ORG_ROLES.filter((role) => {
    // org admin role
    if (role.role === TM_ADMIN_ROLE) {
      return me.isOrgAdmin();
    }

    // team admin role
    if (role.role === TM_TEAM_ADMIN_ROLE) {
      return allowTeamAdminRole;
    }

    return true;
  });
};

export const InviteUserButton = ({
  autocompletedTeams,
  showAddUserModal,
  user,
}) => {
  const flags = useFlags();

  // Do not render this component until we know who the user is
  if (!user?.organization?.settings) {
    return null;
  }

  // Do not show if user is not an organization admin
  if (!user.isOrgAdmin()) {
    return null;
  }

  const showTeamsDropdown = teamFeatureEnabled(user.get('organization').settings);
  const orgRoles = filteredOrgRoles({ me: user });

  return (
    <>
      <Tooltip title={(user.verified === false) ?
        'You must verify your email address before you can invite users.' :
        'Invite a user to your organization.'}
      >
        <Button
          className="mx-2 my-auto"
          color="primary"
          disabled={!user.isVerified()}
          onPress={showAddUserModal}
          size="sm"
        >
          {'Invite'}
        </Button>
      </Tooltip>
      {
        !flags.growthContinousOnboarding && (
          <DSThemeContext.Provider value={{ isSDSThemeEnabled: false }}>
            <AddNonSSOUserModal
              orgRoles={orgRoles}
              showTeamsDropdown={showTeamsDropdown}
              teams={autocompletedTeams}
            />
          </DSThemeContext.Provider>
        )
      }
    </>
  );
};

InviteUserButton.defaultProps = {
  autocompletedTeams: List(),
  showAddUserModal: () => {},
};

InviteUserButton.propTypes = {
  autocompletedTeams: ImmutablePropTypes.list,
  showAddUserModal: PropTypes.func,
  user: userRecords.UserRecord.isRequired,
};

const mapStateToProps = (state) => ({
  autocompletedTeams: state.apps?.teamManagement?.usersView?.get('autocompletedTeams'),
  user: state.user,
});

const mapDispatchToProps = (dispatch) => ({
  showAddUserModal: () => {
    dispatch(modalActions.showModal(inviteConstants.ADD_INVITE_USER_MODAL));
    dispatch(inviteActions.initialize({
      source: 'global-invite',
    }));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(InviteUserButton);
